import ListFormItem from "@arelia-components/form/ListFormItem";
import { Subscription, SubscriptionPlan, SubscriptionPlanName } from "@arelia-types/subscriptions";
import { formatDate } from "@arelia-utils/helpers/dates";
import { List, Button, Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { subDays } from "date-fns";
import { useState } from "react";
import { formatCurrency } from "../../../../utils/helpers/formatCurrency";
import SubscriptionInfoActionsAdmin from "./SubscriptionInfoActionsAdmin";
import { useUserTwilioRegistrationStatusAPI, useCustomerTwilioConfigAPI } from "../../../../features/fans/settings/settings.api";
import EditSmsMessagesDialog from "./AddSMSMessagesDialog";
import { displayPlanName } from "./Helper";

type Props = {
  subscription: Subscription;
  removeSubAvailable: boolean;
};

export default function SubscriptionInfoAdmin({ subscription, removeSubAvailable }: Props) {
  const nextBillingDate = subscription.nextBillingDate ?
    (subscription.plan === SubscriptionPlanName.Starter ?
      subDays(new Date(subscription.nextBillingDate), 1) :
      new Date(subscription.nextBillingDate)) :
    null;

  const { id } = useParams<{ id: string }>();
  const [editSmsModalOpen, setEditSmsModalOpen] = useState(false);

  const { data: userTwilioRegLog } = useUserTwilioRegistrationStatusAPI(id || '');
  const { data: userTwilioConfig } = useCustomerTwilioConfigAPI(id);

  const handleOpenEditModal = () => setEditSmsModalOpen(true);
  const handleCloseEditModal = () => setEditSmsModalOpen(false);

  const showSmsFeatures = userTwilioRegLog?.completed === true &&
    subscription.plan === SubscriptionPlanName.PremiumMyFans;

  return (
    <Box sx={styles.listWrapper}>
      <List sx={styles.list}>
        <ListFormItem label="Plan">
          <Typography>{displayPlanName(subscription.plan, subscription.isFreePlan)}</Typography>
        </ListFormItem>

        <ListFormItem label="Payment">
          <Typography sx={styles.paymentText}>
            <strong>{formatCurrency(subscription.monthlyPayment)}</strong> per month
          </Typography>
        </ListFormItem>

        <ListFormItem label="Paid to date">
          <Typography sx={styles.paymentText}>
            <strong>{formatCurrency(subscription.totalPayedAmount)}</strong>
          </Typography>
        </ListFormItem>

        {nextBillingDate && (
          <ListFormItem
            label={subscription.plan === SubscriptionPlanName.Premium ? "Next Billing Date" : "End Date"}
          >
            <Typography>{formatDate(nextBillingDate, "MM/dd/yyyy")}</Typography>
          </ListFormItem>
        )}

        <ListFormItem label="Status">
          <Typography>{subscription.active ? 'Active' : 'Inactive'}</Typography>
        </ListFormItem>

        {showSmsFeatures && (
          <ListFormItem label="Additional SMS Messages">
            <Box sx={styles.additionalMessagesBox}>
              <Typography sx={styles.messageCount}>
                +{userTwilioConfig?.additionalMessageLimit?.toLocaleString() ?? 0} per month
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleOpenEditModal}
                sx={styles.editButton}
              >
                Edit SMS Messages
              </Button>
            </Box>
          </ListFormItem>
        )}
      </List>

      <SubscriptionInfoActionsAdmin
        removeSubAvailable={removeSubAvailable}
        userId={id || ''}
        isFreePlan={subscription.isFreePlan || subscription.plan === SubscriptionPlanName.Starter}
      />

      {editSmsModalOpen && id && (
        <EditSmsMessagesDialog
          onClose={handleCloseEditModal}
          identityId={id}
          currentAdditionalMessages={userTwilioConfig?.additionalMessageLimit ?? 0}
        />
      )}
    </Box>
  );
}

const styles = {
  listWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "20px"
  },
  list: {
    width: "100%"
  },
  additionalMessagesBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%"
  },
  paymentText: {
    fontWeight: 400
  },
  messageCount: {
    display: "flex",
    alignItems: "center"
  },
  editButton: {
    minWidth: "150px"
  }
};
